/**
 * Answering without touching the screen.
 *
 * These children use a laptop as often as a tablet, and on a laptop the hands
 * are already on the keys. Reaching for a mouse to confirm a choice that was
 * just made with a key is the kind of friction nobody reports as a bug and
 * everybody feels — Sam found it by picking an option, pressing Enter, and
 * watching nothing happen.
 */
import { useEffect } from 'react'

/**
 * Is there a real keyboard and a real pointer?
 *
 * Used only to decide whether to SHOW the shortcuts. The keys themselves
 * always work: a tablet with a keyboard case reports as touch, and refusing
 * its keys because of that would be worse than printing a hint nobody needs.
 *
 * Read once, at module load. A child does not plug a keyboard in halfway
 * through a question, and re-checking on every render costs a layout read.
 */
export const HAS_KEYBOARD: boolean = (() => {
  try {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches
  } catch {
    return false
  }
})()

/**
 * The keys, chosen to match what the screen already shows:
 *
 *   1 2 3 4   pick that option, counting down the list
 *   a b c d   the same, for a hand resting on the left
 *   Enter     submit, once something is chosen
 *   Escape    clear the choice
 *
 * Nothing fires while `busy`, so a double press cannot answer twice, and the
 * picking keys are ignored while the child is typing in a box — otherwise
 * pressing "a" in free recall would try to pick option A instead of typing
 * a letter. Enter is the exception: in a form, Enter means send.
 *
 * Modified presses are left alone. Ctrl+A is select-all and Alt+1 is a
 * browser tab; neither should answer a question.
 */
export function useTrialKeys({
  options = 0, picked = null, onPick, onSubmit, onClear, busy = false,
  armAfterMs = 0,
}: {
  /** How many options there are. Zero for a trial with no list to pick from. */
  options?: number
  picked?: number | null
  onPick?: (index: number) => void
  /** What Enter does. Called only when there is something to submit. */
  onSubmit: () => void
  onClear?: () => void
  busy?: boolean
  /**
   * Ignore keys for this long after the screen appears.
   *
   * A key held down repeats. Without this, leaning on Enter to get through a
   * question would submit the answer AND skip straight past the screen that
   * says what the right answer was — so the one child who most needs to read
   * it is the one who never sees it.
   */
  armAfterMs?: number
}) {
  useEffect(() => {
    const armedAt = performance.now() + armAfterMs
    const onKey = (event: KeyboardEvent) => {
      if (busy) return
      if (performance.now() < armedAt) return
      if (event.ctrlKey || event.metaKey || event.altKey) return

      const target = event.target as HTMLElement | null
      const typing = !!target && (
        target.tagName === 'INPUT' || target.tagName === 'TEXTAREA'
        || target.isContentEditable
      )

      if (event.key === 'Enter') {
        // Nothing chosen and there was a list to choose from: Enter has
        // nothing to send, so let it pass rather than submitting a blank.
        if (options > 0 && picked === null) return
        event.preventDefault()
        onSubmit()
        return
      }
      if (typing) return

      if (event.key === 'Escape' && onClear) {
        event.preventDefault()
        onClear()
        return
      }
      if (!onPick || !options) return

      const key = event.key.toLowerCase()
      const byNumber = '123456789'.indexOf(key)
      const byLetter = 'abcdefghi'.indexOf(key)
      const index = byNumber >= 0 ? byNumber : byLetter
      if (index >= 0 && index < options) {
        event.preventDefault()
        onPick(index)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [options, picked, onPick, onSubmit, onClear, busy, armAfterMs])
}
